import { Permission } from '@bookora/shared';

import type { TourDef, TourStepDef } from './types';

const hasTab = (search: string, tab: string): boolean =>
  new URLSearchParams(search).get('tab') === tab;

/**
 * «شعبه‌ها» — the branches settings tab (`/settings?tab=branches`), four steps:
 * add a branch → who works there → its rooms and hours → the switcher that
 * shows one branch's calendar, till, stock and reports.
 *
 * Gated on the (derived) `branches` plan feature and `branch.manage`: a clinic
 * whose plan has one site has nothing to learn here, and the tour fails
 * closed while plan access is unknown. The switcher step shows only once the
 * clinic HAS a second branch — before that the switcher is not on screen, and
 * a step pointing at an absent anchor is skipped anyway.
 *
 * ── WHY `steps` IS STILL EMPTY ON THE REGISTERED DEF ────────────────────────
 *
 * A tour with steps must also be in `TOUR_ORDER` and in the version record of
 * `registry.test.ts` — both frozen for this build and written by the
 * integrator. Until that one-line change lands, the registered def stays a
 * step-less stub (never offered, never linkable) and the steps wait here in
 * {@link BRANCHES_TOUR_STEPS}, already written and tested against the copy
 * rules (`features/branches/branches-tour.test.ts`). The integrator's change:
 * `registry.ts` → `branches: BRANCHES_TOUR_FILLED` and `'branches'` after
 * `'working-hours'` in `TOUR_ORDER`; `registry.test.ts` → the version record
 * `branches: { version: 1, steps: 'add staff hours switcher' }` and the probe
 * `['/settings', '?tab=branches']`.
 */
export const BRANCHES_TOUR_STEPS: readonly TourStepDef[] = [
  {
    id: 'add',
    anchor: 'branches.add',
    placement: 'bottom-end',
    title: 'شعبهٔ جدید',
    body: 'هر شعبه نشانی، تلفن، اتاق‌ها، صندوق و انبار خودش رو داره. پرونده بیمار و فهرست خدمات در همهٔ شعبه‌ها یکی است.',
  },
  {
    id: 'staff',
    anchor: 'branches.staff',
    placement: 'auto',
    requires: { all: [Permission.STAFF_VIEW] },
    title: 'کارکنان هر شعبه',
    body: 'مشخص کنید چه کسی در کدام شعبه کار می‌کنه. بیماران فقط برای پزشکانی نوبت می‌گیرن که در همان شعبه کار می‌کنن.',
  },
  {
    id: 'hours',
    anchor: 'branches.hours',
    placement: 'auto',
    title: 'ساعت کاری و اتاق‌ها',
    body: 'ساعت کاری هر کارمند و اتاق‌ها برای هر شعبه جدا تنظیم می‌شه؛ برنامهٔ یک شعبه روی شعبه‌های دیگه اثر نمی‌ذاره.',
  },
  {
    id: 'switcher',
    anchor: 'branches.switcher',
    placement: 'bottom-start',
    title: 'نمایش یک شعبه',
    body: (ctx) =>
      ctx.viewport === 'compact'
        ? 'از منو شعبه رو عوض کنید تا تقویم، جلسه‌های درمان، صندوق، انبار و گزارش‌ها فقط اطلاعات همون شعبه رو نمایش بدن.'
        : 'شعبه رو از این‌جا عوض کنید تا تقویم، جلسه‌های درمان، صندوق، انبار و گزارش‌ها فقط اطلاعات همون شعبه رو نمایش بدن.',
  },
];

export const BRANCHES_TOUR: TourDef = {
  key: 'branches',
  version: 1,
  surface: 'page',
  title: 'شعبه‌ها',
  hubDescription: 'شعبهٔ جدید، کارکنان و ساعات کاری هر شعبه و جابه‌جایی میان شعبه‌ها.',
  offer: {
    body: 'در چهار مرحله نشونتون می‌دیم چطور شعبه اضافه کنید و کارکنان و ساعت کاری هر شعبه رو مشخص کنید.',
  },
  route: {
    path: '/settings?tab=branches',
    match: (pathname, search) => pathname === '/settings' && hasTab(search, 'branches'),
  },
  requires: { all: [Permission.BRANCH_MANAGE] },
  requiresFeature: 'branches',
  autoOffer: false,
  maxSteps: 4,
  minSteps: 2,
  // Registered step-less until the integrator lists it (see above).
  steps: [],
};

/** The tour as it ships once `TOUR_ORDER` lists it — what the integrator registers. */
export const BRANCHES_TOUR_FILLED: TourDef = {
  ...BRANCHES_TOUR,
  autoOffer: true,
  steps: BRANCHES_TOUR_STEPS,
};
